/**
 * Autocomplete Client using autosuggest-sdk
 * 
 * This module wraps the autosuggest-sdk to provide autocomplete functionality
 * for the application. The SDK handles normalization internally.
 */

import { createAutosuggestClient } from 'autosuggest-sdk';
import type { IAirportItem } from '../components/Common/AirportSelect/Item';

/**
 * Determine environment from NEXT_PUBLIC_SENTRY_ENV or NEXT_PUBLIC_BACKEND_URL
 */
function getEnvironment(): 'acpt' | 'int' | 'prod' {
	// Check SENTRY_ENV first
	const sentryEnv = process.env.NEXT_PUBLIC_SENTRY_ENV?.toLowerCase();
	if (sentryEnv === 'prod' || sentryEnv === 'production') {
		return 'prod';
	}
	if (sentryEnv === 'int' || sentryEnv === 'integration') {
		return 'int';
	}
	if (sentryEnv === 'acpt' || sentryEnv === 'acceptance' || sentryEnv === 'dev') {
		return 'acpt';
	}

	// Fallback: check BACKEND_URL
	const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || '';
	if (backendUrl.includes('.prod.') || backendUrl.includes('smarterflyer.com')) {
		return 'prod';
	}
	if (backendUrl.includes('.int.')) {
		return 'int';
	}

	// Default to acpt for development
	return 'acpt';
}

// Create the autosuggest client with configuration
// Environment is determined automatically from NEXT_PUBLIC_SENTRY_ENV:
// - prod → production API
// - int → integration API
// - acpt/dev → acceptance API (default)
const client = createAutosuggestClient({
	mode: 'new',
	env: getEnvironment(),
	timeoutMs: 5000,
	debounceMs: 300,
});

/**
 * Fetch autocomplete suggestions for flights
 * 
 * @param query - The search query
 * @param locale - Optional locale code (e.g., 'en-US', 'de-DE')
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Array of airport/city items in legacy format
 */
export async function fetchFlightAutocomplete(
	query: string,
	locale?: string,
	signal?: AbortSignal
): Promise<IAirportItem[]> {
	try {
		const response = await client.fetch({
			query,
			locale: locale || 'en-US',
			vertical: 'flights',
			normalizeResponse: true,
			signal,
		});

		// SDK returns normalized data in legacy format
		if (!response.data || !Array.isArray(response.data)) {
			return [];
		}

		// SDK already provides data in the correct format
		return response.data as IAirportItem[];
	} catch (error: any) {
		if (error?.name !== 'AbortError' && !error?.message?.includes('AbortError')) {
			console.error('Autocomplete SDK error:', error);
		}
		return [];
	}
}

export default {
	fetchFlightAutocomplete,
};
